import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, CheckCheck, AlertCircle, FileText, Image as ImageIcon, Download } from "lucide-react";
import { api, formatApiError, API_BASE } from "../lib/api";
import { tr, LANG } from "../lib/i18n";

const Tick = ({ s }) => {
  if (s === "read") return <CheckCheck size={14} className="text-sky-600" aria-label={tr("ch.read")} />;
  if (s === "delivered") return <CheckCheck size={14} aria-label={tr("ch.delivered")} />;
  if (s === "failed") return <AlertCircle size={14} className="text-red-600" aria-label={tr("ch.failed")} />;
  return <Check size={14} aria-label={tr("ch.sent")} />;
};

function Bubble({ m }) {
  const out = m.direction === "out";
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
      <div className={`max-w-[80%] px-3 py-2 text-sm ${out ? "bg-emerald-50 border border-emerald-200" : "bg-white border border-[var(--divider)]"}`}>
        {m.type === "image" && <div className="flex items-center gap-1 text-xs opacity-70"><ImageIcon size={14} />{tr("ch.photo")}</div>}
        {m.type === "document" && <div className="flex items-center gap-1 text-xs opacity-70"><FileText size={14} />{m.filename || tr("ch.document")}</div>}
        {m.template && <div className="text-xs opacity-80">{tr("ch.template")}: {m.template}</div>}
        <div className="whitespace-pre-wrap" dir="auto">{m.body}</div>
        <div className="flex items-center justify-end gap-1 text-[11px] opacity-80 mt-1" dir="ltr">
          {out && <span>{m.bot ? tr("ch.auto_answer") : m.by_name}</span>}<span>{(m.at || "").slice(11, 16)}</span>{out && <Tick s={m.status} />}
        </div>
        {m.status === "failed" && m.error && <div className="text-xs text-red-700">{m.error}</div>}
      </div>
    </div>
  );
}

export function ChatPanel({ lead, isAdmin }) {
  const [st, setSt] = useState(null);
  const [text, setText] = useState("");
  const [tpl, setTpl] = useState("");
  const [params, setParams] = useState([]);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const load = async () => {
    try { const { data } = await api.get(`/leads/${lead.id}/messages`); setSt(data); } catch { /* polling */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [st?.messages?.length]);
  if (!st) return null;
  if (!st.enabled) return <div className="p-4 border border-[var(--divider)] text-sm" data-testid="chat-locked">{tr("ch.locked")}</div>;
  if (!st.connected) return <div className="p-4 border border-[var(--divider)] text-sm" data-testid="chat-not-connected">{tr("ch.not_connected")}</div>;
  const t = st.templates.find((x) => `${x.name}|${x.language}` === tpl);
  const send = async (body) => {
    setBusy(true);
    try { await api.post(`/leads/${lead.id}/messages`, body); setText(""); setTpl(""); setParams([]); await load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const attach = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(file.type.startsWith("image/") ? "/uploads/photo" : "/uploads/document", fd);
      const url = data.url?.startsWith("http") ? data.url : `${API_BASE.replace(/\/api$/, "")}${data.url}`;
      await send({ media_url: url, media_type: file.type.startsWith("image/") ? "image" : "document", filename: file.name, text });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border border-[var(--divider)] flex flex-col" data-testid="chat-panel">
      <div className="p-3 border-b border-[var(--divider)] flex items-center justify-between text-sm">
        <span className="font-medium">{tr("ch.title")}</span>
        <span data-testid="chat-window" className={st.window_open && !st.stop_blocked ? "text-emerald-700" : "text-amber-700"}>
          {st.stop_blocked ? tr("ch.stop_blocked") : st.window_open ? tr("ch.window_open") : tr("ch.window_closed")}
        </span>
        {isAdmin && <a className="btn-ghost text-xs" href={`${API_BASE}/leads/${lead.id}/chat.pdf`} onClick={async (e) => {
          e.preventDefault();
          const r = await api.get(`/leads/${lead.id}/chat.pdf`, { responseType: "blob" });
          const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `chat-${lead.id.slice(0, 8)}.pdf`; a.click();
        }} data-testid="chat-export"><Download size={14} /> PDF</a>}
      </div>
      <div ref={box} className="p-3 space-y-2 overflow-y-auto bg-[var(--surface-2,#f6f6f4)]" style={{ maxHeight: 380, minHeight: 160 }} data-testid="chat-messages">
        {st.messages.map((m) => <Bubble key={m.id} m={m} />)}
        {!st.messages.length && <div className="text-sm text-muted-foreground text-center py-6">{tr("ch.empty")}</div>}
      </div>
      {st.opted_out && <div className="px-3 py-2 text-xs bg-amber-50 text-amber-900" data-testid="chat-opted-out">{tr("ch.opted_out")}</div>}
      {st.window_open && !st.stop_blocked ? (
        <div className="p-3 space-y-2">
          <div className="flex flex-wrap gap-1">
            {st.quick_replies.map((q, i) => (
              <button key={i} type="button" className="text-xs px-2 py-1 border border-[var(--divider)] hover:bg-[var(--surface-2,#f6f6f4)]" onClick={() => setText(q[LANG] || q.en)} data-testid={`quick-reply-${i}`}>
                {(q[LANG] || q.en).slice(0, 38)}…
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <textarea className="input flex-1" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={tr("ch.type")} data-testid="chat-input" />
            <div className="flex flex-col gap-1">
              <button className="btn-primary" disabled={busy || !text.trim()} onClick={() => send({ text })} data-testid="chat-send">{tr("ch.send")}</button>
              <label className="btn-secondary cursor-pointer text-xs" data-testid="chat-attach">{tr("ch.attach")}
                <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => e.target.files[0] && attach(e.target.files[0])} />
              </label>
            </div>
          </div>
        </div>
      ) : st.stop_blocked ? null : (
        <div className="p-3 space-y-2" data-testid="chat-template-box">
          <p className="text-xs text-muted-foreground">{tr("ch.why_template")}</p>
          <select className="input h-10 w-full" value={tpl} onChange={(e) => { setTpl(e.target.value); setParams([]); }} data-testid="chat-template-select">
            <option value="">{tr("ch.pick_template")}</option>
            {st.templates.map((x) => <option key={`${x.name}|${x.language}`} value={`${x.name}|${x.language}`}>{x.name} ({x.language})</option>)}
          </select>
          {t && <p className="text-xs text-muted-foreground whitespace-pre-wrap" data-testid="chat-template-text">{t.text}</p>}
          {t && Array.from({ length: t.params || 0 }).map((_, i) => (
            <input key={i} className="input w-full" placeholder={`{{${i + 1}}}`} value={params[i] || ""} onChange={(e) => { const p = [...params]; p[i] = e.target.value; setParams(p); }} data-testid={`chat-template-param-${i}`} />
          ))}
          <button className="btn-primary" disabled={!t || busy} onClick={() => send({ template: t.name, template_lang: t.language, params })} data-testid="chat-send-template">{tr("ch.send_template")}</button>
        </div>
      )}
    </div>
  );
}
